<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    create-to="/admin/keys/new"
    create-label="Crear llave"
    create-icon="mdi-key-plus"
    remove-title="Eliminar llave"
  />
</template>

<script setup lang="ts">
import type { Key } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { keyTypeLabel } from "@/utils/labels";

const view = useIndexView<Key, "id" | "alias" | "type" | "user_username">({
  title: "Índice de llaves",
  rowKey: "id",
  fetchPage: (params) => banco.admin.keys.find(params),
  remove: (item) => banco.admin.keys.destroy(item.id),
  removedMessage: (item) => `Se eliminó la llave ${item.alias ?? item.id}.`,
  filters: [
    { field: "id", label: "ID" },
    { field: "alias", label: "Alias" },
    { field: "type", label: "Tipo" },
    { field: "user_username", label: "Usuario dueño" },
  ],
  confirm: {
    expected: (item) => item.alias ?? String(item.id),
    label: "Alias de la llave",
    hint: "Para confirmar ingresa el <b>alias</b> de la llave a eliminar.",
    error: "El alias de la llave no es correcto.",
    warning: (item) =>
      `Esta acción eliminará la llave ${item.alias ?? item.id}, sus CSR y sus certificados.`,
  },
});

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "ID", value: "id", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/keys/{id}/edit" },
  },
  { header: { text: "Alias", value: "alias", class: "text-no-wrap" }, row: { type: "TEXT" } },
  {
    header: { text: "Tipo", value: "type", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { PERSISTENT: "primary", WRAPPING: "purple", WRAPPED: "teal" },
      transform: (val) => keyTypeLabel(val),
    },
  },
  {
    header: { text: "Dueño", value: "user_id", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/users/{user_id}/edit" },
  },
  {
    header: { text: "CSR", value: "id", class: "text-no-wrap", sortable: false },
    row: { type: "LINK", to: "/admin/keys/{id}/csr", transform: () => "Ir a CSR" },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        { label: "Editar", icon: "mdi-pencil", link: "/admin/keys/{id}/edit" },
        { label: "Ver CSR", icon: "mdi-file-certificate", link: "/admin/keys/{id}/csr" },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as Key),
        },
      ],
    },
  },
];
</script>
